import React from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { Card, CardContent } from './ui/Card';
import { ScanIcon, PaletteIcon, SparklesIcon, SofaIcon, CameraIcon, WandIcon } from 'lucide-react';
import { Button } from './ui/Button';
export function InteriorAISection() {
  const revealRef = useScrollReveal();
  const features = [{
    icon: <CameraIcon className="w-6 h-6" data-id="element-194" />,
    title: 'Odfotíte Izbu',
    desc: 'Stačí jedna fotka vášho priestoru. AI rozpozná rozmery, nábytok, osvetlenie a štýl.',
    color: 'text-blue-500',
    bg: 'bg-blue-50'
  }, {
    icon: <SparklesIcon className="w-6 h-6" data-id="element-195" />,
    title: 'AI Navrhne Dizajn',
    desc: 'Gemini vygeneruje 5 návrhov v rôznych štýloch — od minimalizmu po luxus. Za sekúnd.',
    color: 'text-purple-500',
    bg: 'bg-purple-50'
  }, {
    icon: <PaletteIcon className="w-6 h-6" data-id="element-196" />,
    title: 'Prispôsobte Farby',
    desc: 'Interaktívne meňte farby stien, podláh a doplnkov. AI zachová harmóniu celého priestoru.',
    color: 'text-amber-500',
    bg: 'bg-amber-50'
  }, {
    icon: <SofaIcon className="w-6 h-6" data-id="element-197" />,
    title: 'Virtuálny Nábytok',
    desc: 'Vložte reálne produkty z katalógov priamo do vášho priestoru. Vidíte ako vyzerajú ešte pred kúpou.',
    color: 'text-emerald-500',
    bg: 'bg-emerald-50'
  }, {
    icon: <ScanIcon className="w-6 h-6" data-id="element-198" />,
    title: '3D Vizualizácia',
    desc: 'Prechádzajte sa po novom interiéri vo virtuálnej realite. Zažite priestor ešte pred realizáciou.',
    color: 'text-rose-500',
    bg: 'bg-rose-50'
  }, {
    icon: <WandIcon className="w-6 h-6" data-id="element-199" />,
    title: 'Rozpočet & Nákup',
    desc: 'AI vypočíta náklady a prepojí vás priamo s dodávateľmi. Od návrhu po realizáciu na jedno kliknutie.',
    color: 'text-indigo-500',
    bg: 'bg-indigo-50'
  }];
  const styles = [{
    name: 'Minimalizmus',
    image: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=400&h=300&fit=crop&crop=center'
  }, {
    name: 'Škandinávsky',
    image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400&h=300&fit=crop&crop=center'
  }, {
    name: 'Industriálny',
    image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&h=300&fit=crop&crop=center'
  }, {
    name: 'Luxusný',
    image: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=400&h=300&fit=crop&crop=center'
  }];
  return <section id="interiorai" ref={revealRef} className="py-36 px-4 sm:px-6 lg:px-8 overflow-hidden" data-id="element-200">
      <div className="max-w-7xl mx-auto" data-id="element-201">
        {/* Header */}
        <div className="reveal text-center mb-10" data-id="element-202">
          <p className="text-xs font-semibold tracking-[0.25em] text-apple-gray uppercase mb-6" data-id="element-203">
            NOVÝ PRODUKT
          </p>
          <h2 className="text-4xl md:text-7xl font-extrabold tracking-tight text-apple-black mb-6 leading-tight" data-id="element-204">
            Larsen Evans
            <br data-id="element-205" />
            <span className="text-gradient-blue" data-id="element-206">InteriorAI</span>
          </h2>
          <p className="text-xl md:text-2xl text-apple-gray font-light max-w-3xl mx-auto leading-relaxed" data-id="element-207">
            Odfotíte izbu. AI navrhne interiér. Vy si vyberiete štýl. Revolúcia
            v interiérovom dizajne poháňaná Gemini multimodálnou AI.
          </p>
        </div>

        {/* Hero Image */}
        <div className="reveal reveal-delay-1 max-w-5xl mx-auto mb-28" data-id="element-208">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl" data-id="element-209">
            <img src="https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=1200&h=600&fit=crop&crop=center" alt="InteriorAI vizualizácia" className="w-full h-64 md:h-[480px] object-cover" data-id="element-210" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" data-id="element-211" />
            <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12" data-id="element-212">
              <div className="flex flex-wrap gap-3 mb-4" data-id="element-213">
                <span className="bg-white/20 backdrop-blur-md text-white text-xs font-semibold px-4 py-1.5 rounded-full border border-white/20" data-id="element-214">
                  Gemini 2.0
                </span>
                <span className="bg-white/20 backdrop-blur-md text-white text-xs font-semibold px-4 py-1.5 rounded-full border border-white/20" data-id="element-215">
                  PWA Ready
                </span>
                <span className="bg-white/20 backdrop-blur-md text-white text-xs font-semibold px-4 py-1.5 rounded-full border border-white/20" data-id="element-216">
                  Real-time Rendering
                </span>
              </div>
              <p className="text-white/80 text-sm md:text-base font-light max-w-xl" data-id="element-217">
                AI analyzuje váš priestor a za sekundy vytvorí fotorealistické
                návrhy interiéru v ľubovoľnom štýle.
              </p>
            </div>
          </div>
        </div>

        {/* Interactive Device Showcase */}
        <div className="reveal reveal-delay-2 relative w-full max-w-5xl mx-auto h-auto md:h-[420px] mb-28 flex flex-col md:flex-row items-center justify-center gap-8 md:gap-6" data-id="element-218">
          {/* Phone — Room Scanner */}
          <div className="animate-float-delayed w-48 md:w-44 h-80 bg-white rounded-[2rem] border-4 border-gray-100 shadow-2xl flex flex-col items-center relative z-20 md:transform md:-rotate-6 md:translate-x-4 card-hover-premium overflow-hidden" data-id="element-219">
            <div className="w-1/2 h-1 bg-gray-200 rounded-full absolute top-4 z-10" data-id="element-220"></div>
            <img src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=200&h=300&fit=crop&crop=center" alt="InteriorAI Room Scan" className="w-full h-full object-cover absolute inset-0 opacity-30" data-id="element-221" />
            <div className="relative z-10 flex flex-col items-center justify-center h-full gap-2" data-id="element-222">
              <CameraIcon className="w-8 h-8 text-apple-blue mb-1" data-id="element-223" />
              <span className="text-apple-black font-bold text-sm" data-id="element-224">
                InteriorAI
              </span>
              <span className="text-apple-gray text-xs font-medium" data-id="element-225">
                Room Scanner
              </span>
              <div className="flex gap-1 mt-2" data-id="element-226">
                <div className="w-2 h-2 rounded-full bg-apple-blue animate-pulse" data-id="element-227"></div>
                <div className="w-2 h-2 rounded-full bg-purple-500" data-id="element-228"></div>
              </div>
              <span className="text-[10px] text-apple-gray mt-3 bg-white/80 backdrop-blur-sm px-3 py-1 rounded-full" data-id="element-229">
                📸 Scan & Design
              </span>
            </div>
          </div>

          {/* Desktop — AI Dashboard */}
          <div className="animate-float w-full max-w-sm md:max-w-none md:w-[420px] h-60 md:h-80 bg-white rounded-xl md:rounded-[2rem] border-4 border-gray-100 shadow-2xl flex flex-col relative z-30 card-hover-premium overflow-hidden" data-id="element-230">
            <div className="w-full h-7 bg-gray-50 border-b border-gray-100 rounded-t-lg md:rounded-t-[1.75rem] flex items-center px-4 gap-1.5 flex-shrink-0" data-id="element-231">
              <div className="w-2 h-2 rounded-full bg-red-400" data-id="element-232"></div>
              <div className="w-2 h-2 rounded-full bg-amber-400" data-id="element-233"></div>
              <div className="w-2 h-2 rounded-full bg-green-400" data-id="element-234"></div>
              <span className="text-[10px] text-apple-gray ml-auto font-mono" data-id="element-235">
                interiorai.app
              </span>
            </div>
            <div className="flex-1 flex" data-id="element-236">
              {/* Sidebar */}
              <div className="w-20 md:w-24 bg-gray-50 border-r border-gray-100 p-3 flex flex-col gap-2" data-id="element-237">
                <div className="w-full h-2 bg-apple-blue/20 rounded-full" data-id="element-238"></div>
                <div className="w-3/4 h-2 bg-gray-200 rounded-full" data-id="element-239"></div>
                <div className="w-full h-2 bg-gray-200 rounded-full" data-id="element-240"></div>
                <div className="w-2/3 h-2 bg-purple-200 rounded-full" data-id="element-241"></div>
                <div className="mt-auto" data-id="element-242">
                  <SparklesIcon className="w-4 h-4 text-apple-blue mx-auto" data-id="element-243" />
                </div>
              </div>
              {/* Main content */}
              <div className="flex-1 p-4 flex flex-col items-center justify-center gap-3" data-id="element-244">
                <SparklesIcon className="w-8 h-8 text-apple-blue" data-id="element-245" />
                <span className="text-apple-black font-bold text-base" data-id="element-246">
                  InteriorAI Dashboard
                </span>
                <div className="flex gap-2" data-id="element-247">
                  <div className="w-3 h-3 rounded-full bg-apple-blue" data-id="element-248"></div>
                  <div className="w-3 h-3 rounded-full bg-emerald-500" data-id="element-249"></div>
                  <div className="w-3 h-3 rounded-full bg-amber-500" data-id="element-250"></div>
                </div>
                <div className="flex gap-1 mt-1" data-id="element-251">
                  <div className="h-6 w-8 bg-blue-100 rounded" data-id="element-252"></div>
                  <div className="h-6 w-8 bg-emerald-100 rounded" data-id="element-253"></div>
                  <div className="h-6 w-8 bg-amber-100 rounded" data-id="element-254"></div>
                  <div className="h-6 w-8 bg-rose-100 rounded" data-id="element-255"></div>
                </div>
                <span className="text-[10px] text-apple-gray mt-1" data-id="element-256">
                  5 návrhov vygenerovaných
                </span>
              </div>
            </div>
          </div>

          {/* Tablet — 3D Visualization */}
          <div className="animate-float-slow w-64 md:w-56 h-72 md:h-80 bg-white rounded-[1.5rem] md:rounded-[2rem] border-4 border-gray-100 shadow-2xl flex flex-col items-center relative z-10 md:transform md:rotate-6 md:-translate-x-4 card-hover-premium overflow-hidden" data-id="element-257">
            <div className="w-1 h-1/4 bg-gray-200 rounded-full absolute left-2 z-10" data-id="element-258"></div>
            <img src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=300&h=400&fit=crop&crop=center" alt="InteriorAI 3D View" className="w-full h-full object-cover absolute inset-0 opacity-25" data-id="element-259" />
            <div className="relative z-10 flex flex-col items-center justify-center h-full gap-2" data-id="element-260">
              <ScanIcon className="w-8 h-8 text-rose-500 mb-1" data-id="element-261" />
              <span className="text-apple-black font-bold text-sm" data-id="element-262">
                InteriorAI
              </span>
              <span className="text-apple-gray text-xs font-medium" data-id="element-263">
                3D Vizualizácia
              </span>
              <div className="flex gap-1.5 mt-2" data-id="element-264">
                <div className="w-2.5 h-2.5 rounded-full bg-rose-500" data-id="element-265"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-apple-blue" data-id="element-266"></div>
              </div>
              <span className="text-[10px] text-apple-gray mt-3 bg-white/80 backdrop-blur-sm px-3 py-1 rounded-full" data-id="element-267">
                🏠 Walk-through
              </span>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="reveal mb-28" data-id="element-268">
          <h3 className="text-3xl md:text-4xl font-bold text-apple-black text-center mb-16 tracking-tight" data-id="element-269">
            Ako to funguje
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-270">
            {features.map((feature, i) => <Card key={i} className="border-gray-100 rounded-2xl bg-white/80 backdrop-blur-sm shadow-sm card-hover-premium" data-id="element-271">
                <CardContent className="p-8" data-id="element-272">
                  <div className={`w-12 h-12 ${feature.bg} rounded-xl flex items-center justify-center mb-5`} data-id="element-273">
                    <div className={feature.color} data-id="element-274">{feature.icon}</div>
                  </div>
                  <h4 className="text-lg font-bold text-apple-black mb-3" data-id="element-275">
                    {feature.title}
                  </h4>
                  <p className="text-apple-gray font-light leading-relaxed text-sm" data-id="element-276">
                    {feature.desc}
                  </p>
                </CardContent>
              </Card>)}
          </div>
        </div>

        {/* Style Gallery */}
        <div className="reveal mb-28" data-id="element-277">
          <h3 className="text-3xl md:text-4xl font-bold text-apple-black text-center mb-6 tracking-tight" data-id="element-278">
            Vyberte si štýl
          </h3>
          <p className="text-apple-gray text-center font-light mb-14 max-w-2xl mx-auto" data-id="element-279">
            AI rozumie desiatkam interiérových štýlov. Stačí povedať alebo
            ukázať, čo sa vám páči.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-id="element-280">
            {styles.map((style, i) => <div key={i} className="group relative rounded-2xl overflow-hidden shadow-md card-hover-premium cursor-pointer" data-id="element-281">
                <img src={style.image} alt={style.name} className="w-full h-48 md:h-56 object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-282" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" data-id="element-283" />
                <span className="absolute bottom-4 left-4 text-white font-bold text-sm md:text-base" data-id="element-284">
                  {style.name}
                </span>
              </div>)}
          </div>
        </div>

        {/* CTA */}
        <div className="reveal text-center" data-id="element-285">
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-12 md:p-16 border border-gray-200" data-id="element-286">
            <h3 className="text-3xl md:text-5xl font-extrabold tracking-tight text-apple-black mb-6" data-id="element-287">
              Chcete InteriorAI pre váš biznis?
            </h3>
            <p className="text-lg text-apple-gray font-light max-w-2xl mx-auto mb-10 leading-relaxed" data-id="element-288">
              Či ste realitná kancelária, dizajnérske štúdio alebo e-shop s
              nábytkom — InteriorAI sa prispôsobí vašim potrebám.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-289">
              <a href="https://interiorai.larsenevans.com" target="_blank" rel="noopener noreferrer" data-id="element-290">
                <Button size="large" className="!bg-apple-blue hover:!bg-apple-blueHover !text-white !rounded-full !px-10 !py-4 !font-bold w-full sm:w-auto btn-hover-premium !border-none" data-id="element-291">
                  Dohodnúť Demo
                </Button>
              </a>
              <a href="https://gruppa.cloud/privacy/" target="_blank" rel="noopener noreferrer" data-id="element-292">
                <Button size="large" className="!bg-white hover:!bg-gray-50 !text-apple-black !border-2 !border-gray-200 !rounded-full !px-10 !py-4 !font-bold w-full sm:w-auto card-hover-premium" data-id="element-293">
                  Ochrana súkromia
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>;
}